import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

export const CHUNK_RELOAD_KEY = 'scrudix:chunk-reload-once';

export function isDynamicImportChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /Failed to fetch dynamically imported module|Importing a module script failed|Loading chunk [\w-]+ failed|error loading dynamically imported module|failed to load module script/i.test(message);
}

export function clearChunkReloadFlag(): void {
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    } catch {
      // ignore quota or security exceptions
    }
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export async function purgeServiceWorkerCaches(): Promise<void> {
  if (typeof caches !== 'undefined') {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    } catch {
      // ignore
    }
  }
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((r) => r.unregister()));
    } catch {
      // ignore
    }
  }
}

export function forceHardReload(): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_v', Date.now().toString());
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

export function lazyWithRetry<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const loaded = await importer();
      clearChunkReloadFlag();
      return loaded;
    } catch (firstError) {
      if (!isDynamicImportChunkError(firstError)) {
        throw firstError;
      }

      // One immediate retry can recover transient CDN/network blips.
      try {
        await sleep(250);
        const loaded = await importer();
        clearChunkReloadFlag();
        return loaded;
      } catch (secondError) {
        if (typeof window !== 'undefined') {
          const alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY) === '1';
          if (!alreadyReloaded) {
            sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
            await purgeServiceWorkerCaches();
            forceHardReload();
            return new Promise(() => {}); // Pause component evaluation while reloading
          }
        }

        throw secondError;
      }
    }
  });
}
